import { Group, Stack } from "@mantine/core"
import { RowSelectionState } from "@tanstack/react-table"
import {
  ExportButton,
  ExportType,
  InfoBox,
  Loader,
  ColumnManager,
} from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"
import { removeUndefined } from "@tfg/types/schemas/utils"

import { InlineBanner } from "~/components/InlineBanner"
import { SortBuilder } from "~/components/SortBuilder"
import { CustomTable } from "~/components/table/CustomTable"
import { FilterBuilderWrapper } from "~/components/table/FilterBuilderWrapper"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindProductsProductStandardDto,
  FindProductsStandardsQueryResponse,
  FindProductsStandardsRequestFiltersDto,
  useFindProductsStandards,
  useFindProductStandardValues,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/hooks/useGetOrganizationSettingsQuery"
import { useBuildStandardsSorts } from "~/pages/Brand/Products/List/Components/ProductsTables/standards/hooks/useBuildStandardsSorts"
import { useStandardsTable } from "~/pages/Brand/Products/List/Components/ProductsTables/standards/useStandardsTable"
import { StandardComplianceDrawer } from "~/pages/Brand/Products/List/Components/StandardDrawer.tsx"
import { useStandardsHeaders } from "~/pages/Brand/Products/List/Components/headers.tsx"
import { useBuildProductFilters } from "~/pages/Brand/Products/List/hooks/useBuildProductFilters"
import { useDownloadProductsCertificatesCSVMutation } from "~/pages/Brand/Products/List/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

export const StandardsProductsTable = () => {
  const { t } = useTranslation(["products", "common"])
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { user, isAdmin } = useAuth()
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)

  const { title, searchPlaceholder, options, filtersValue, setFiltersValue } =
    useBuildProductFilters<FindProductsStandardsRequestFiltersDto>()

  const { sorts, setSorts, sortOptions } = useBuildStandardsSorts()
  const {
    data: productsData,
    isPending: isLoadingProducts,
    isFetching: isFetchingProducts,
    setPageBasedPagination,
    pageBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindProductsStandards,
    FindProductsStandardsQueryResponse
  >(useFindProductsStandards, {
    filters: filtersValue,
    sorts,
  })

  const products = productsData?.data ?? []

  const standardHeaders = useStandardsHeaders(
    products,
    filtersValue?.standardId ? Number(filtersValue.standardId) : undefined
  )

  const [selectedStandard, setSelectedStandard] = useState<
    FindProductsProductStandardDto | undefined
  >(undefined)

  const [selectedProductId, setSelectedProductId] = useState<
    string | undefined
  >(undefined)

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const table = useStandardsTable({
    pagination: pageBasedPagination,
    rowSelection,
    setPagination: setPageBasedPagination,
    setRowSelection,
    pageCount: totalPages,
    standardHeaders,
    onStandardClick: (standard, productId) => {
      setSelectedStandard(standard)
      setSelectedProductId(productId)
    },
    products,
  })

  const {
    mutate: exportCertificateView,
    isPending: isCertificatesExportLoading,
  } = useDownloadProductsCertificatesCSVMutation({
    filters: filtersValue,
  })
  const { data: standardValues } = useFindProductStandardValues(
    removeUndefined({ ...filtersValue, standardId: undefined })
  )

  let adminMessage: string | null = null

  if (org?.settings?.certificatesPlan !== "EXPERT") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("simulation.notSubscribed.title")}
          message={t("simulation.notSubscribed.message")}
          variant="info"
        />
      )
    }
  }

  return (
    <Stack>
      {adminMessage && (
        <InlineBanner
          type="admin"
          size="xs"
          message={t("simulation.notSubscribed.title")}
        >
          {adminMessage}
        </InlineBanner>
      )}
      <Views tableContext={TABLE_CONTEXT_MAP.PRODUCTS_CERTIFICATES} />
      <Group>
        <SearchBar
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          placeholder={t("products:lists.placeholders.searchProduct")}
          isFetching={isFetchingProducts}
        />
        <SortBuilder sorts={sorts} setSorts={setSorts} options={sortOptions} />
        <FilterBuilderWrapper
          title={title}
          searchPlaceholder={searchPlaceholder}
          options={[
            ...options,
            {
              label: t("lists.placeholders.standard"),
              key: "standardId",
              options:
                standardValues?.data.map((standard) => ({
                  label: standard.name,
                  value: standard.id.toString(),
                })) ?? [],
              type: "select",
            },
          ]}
          setFilters={setFiltersValue}
          filters={filtersValue}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <ViewHeader tableContext={TABLE_CONTEXT_MAP.PRODUCTS_CERTIFICATES} />
        <Group style={{ marginLeft: "auto" }}>
          <ExportButton
            label={t("lists.export.view")}
            exportOptions={[
              {
                type: ExportType.CSV,
                label: t("common:actionButtons.exportAsCsv"),
                exportFunction: exportCertificateView,
                isLoading: isCertificatesExportLoading,
              },
            ]}
          />
        </Group>
      </Group>
      {isLoadingProducts ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          manualPaginationCount={productsData?.count || 0}
          stickyHeader
          layout="fixed"
          setOpenColumnManager={setIsColumnManagerOpen}
        />
      )}
      {selectedStandard && selectedProductId ? (
        <StandardComplianceDrawer
          standard={selectedStandard}
          productId={selectedProductId}
          onClose={() => setSelectedStandard(undefined)}
        />
      ) : null}
    </Stack>
  )
}
